import type {
    LomdaV3Block,
    LomdaV3Content,
} from "@/lib/utils/lomdaV3/types";
import { cn } from "@/lib/utils";
import { TITLES_PRESETS } from "./theme";
import { variantLabelKey } from "./blockRegistry";

const FONT_ROLE = { fontHeading: "titles", fontBody: "body" } as const;

export type ThemeFontKey = keyof typeof FONT_ROLE;
export type FontRole = (typeof FONT_ROLE)[ThemeFontKey];

export const THEME_FONT_KEYS = Object.keys(FONT_ROLE) as ThemeFontKey[];

export const fontRoleOf = (key: ThemeFontKey): FontRole => FONT_ROLE[key];

/** A rich-text field saved under this key renders every line in the titles
 *  font, whatever its tag: heading-text and column-card headings, media
 *  captions, walkthrough steps. */
export const HEADING_FIELD = "heading";
export const HEADING_FIELD_CLASS = "lomda-v3-heading";

type HeadingValue = Record<typeof HEADING_FIELD, string>;

/** A heading field's RichText props. The scan finds it by key, the screen
 *  styles it by class: both come from here. */
export const headingFieldProps = (
    value: HeadingValue,
    save: (patch: HeadingValue) => void,
    className?: string
) => ({
    initialHtml: value[HEADING_FIELD],
    editorClassName: cn(HEADING_FIELD_CLASS, className),
    onCommit: (html: string) => save({ [HEADING_FIELD]: html }),
});

export const headingHtmlProps = (value: HeadingValue) => ({
    html: value[HEADING_FIELD],
    className: HEADING_FIELD_CLASS,
});

/** The font group a line renders in (richText.css). */
export const lineRole = (tag: string, inHeadingField: boolean): FontRole =>
    inHeadingField || TITLES_PRESETS.has(tag) ? "titles" : "body";

const LINES = "h1, h2, h3, h4, h5, h6, p";
const OWN_FONT = '[style*="font-family"]';

const lineTag = (line: Element) =>
    line.classList.contains("lomda-v3-label")
        ? "label"
        : line.tagName.toLowerCase();

/** Written the way the editor writes styles (`a: 1; b: 2;`), so loading the
 *  result into the editor changes nothing. */
function dropFontFamily(el: Element) {
    const rest = (el.getAttribute("style") ?? "")
        .split(";")
        .map((d) => d.trim())
        .filter((d) => d && !/^font-family\s*:/i.test(d));
    if (rest.length)
        el.setAttribute("style", rest.map((d) => `${d};`).join(" "));
    else el.removeAttribute("style");
    if (el.tagName === "SPAN" && el.attributes.length === 0)
        el.replaceWith(...Array.from(el.childNodes));
}

let template: HTMLTemplateElement | null = null;
// One inert template, reused: a DOMParser document per string costs far more.
const parse = (html: string) => {
    template ??= document.createElement("template");
    template.innerHTML = html;
    return template;
};

const mayHaveOwnFont = (value: string) =>
    value.includes("font-family") && value.includes("<");

// The HTML block's code is the author's own CSS, never touched.
const isHtmlBlock = (obj: object) =>
    "type" in obj && obj.type === "html" && "data" in obj;

/** Calls `fn` with the innermost block that holds each string, so a text inside
 *  a column is reported as that text block, not as the columns block. */
function forEachBlockText(
    blocks: unknown,
    fn: (html: string, block: LomdaV3Block, inHeadingField: boolean) => void
) {
    if (!Array.isArray(blocks)) return;
    for (const block of blocks as LomdaV3Block[]) {
        if (isHtmlBlock(block)) continue;
        const walk = (value: unknown, key?: string): void => {
            if (key === "blocks") forEachBlockText(value, fn);
            else if (typeof value === "string") {
                if (mayHaveOwnFont(value))
                    fn(value, block, key === HEADING_FIELD);
            } else if (value && typeof value === "object")
                for (const [k, v] of Object.entries(value)) walk(v, k);
        };
        walk(block);
    }
}

/** Returns the same reference when nothing changed, so untouched blocks stay
 *  shared in the undo history. */
function mapBlockText(
    blocks: LomdaV3Block[],
    fn: (html: string, inHeadingField: boolean) => string
): LomdaV3Block[] {
    const map = (value: unknown, key?: string): unknown => {
        if (key === "blocks" && Array.isArray(value))
            return mapBlockText(value, fn);
        if (typeof value === "string")
            return mayHaveOwnFont(value)
                ? fn(value, key === HEADING_FIELD)
                : value;
        if (!value || typeof value !== "object") return value;
        let changed = false;
        const entries = Object.entries(value).map(([k, v]) => {
            const next = map(v, k);
            if (next !== v) changed = true;
            return [k, next] as const;
        });
        if (!changed) return value;
        return Array.isArray(value)
            ? entries.map(([, v]) => v)
            : Object.fromEntries(entries);
    };
    const next = blocks.map((b) =>
        isHtmlBlock(b) ? b : (map(b) as LomdaV3Block)
    );
    return next.some((b, i) => b !== blocks[i]) ? next : blocks;
}

export type OwnFontPlace = Pick<LomdaV3Block, "id" | "type"> & {
    page: number;
    variantLabelKey?: string;
};

type Scanned = { lines: number; html: string };

/** One scan for the prompt: `count` is texts (lines), `places` one entry per
 *  block holding them, and `cleaned` what Switch writes back, so Switch never
 *  scans again. */
export function findOwnFonts(
    content: LomdaV3Content,
    roles: readonly FontRole[]
) {
    let count = 0;
    const places = new Map<string, OwnFontPlace>();
    // Per field kind (index 1 = heading field), keyed by the original html.
    const cleaned = [new Map<string, Scanned>(), new Map<string, Scanned>()];
    content.pages.forEach((page, i) =>
        forEachBlockText(page.blocks, (html, block, inHeadingField) => {
            const memo = cleaned[inHeadingField ? 1 : 0];
            let hit = memo.get(html);
            if (!hit) {
                const tpl = parse(html);
                const lines = Array.from(
                    tpl.content.querySelectorAll(LINES)
                ).filter(
                    (line) =>
                        roles.includes(
                            lineRole(lineTag(line), inHeadingField)
                        ) &&
                        (line.matches(OWN_FONT) ||
                            !!line.querySelector(OWN_FONT))
                );
                for (const line of lines) {
                    line.querySelectorAll(OWN_FONT).forEach(dropFontFamily);
                    if (line.matches(OWN_FONT)) dropFontFamily(line);
                }
                hit = {
                    lines: lines.length,
                    html: lines.length ? tpl.innerHTML : html,
                };
                memo.set(html, hit);
            }
            if (hit.lines === 0) return;
            count += hit.lines;
            if (!places.has(block.id))
                places.set(block.id, {
                    id: block.id,
                    type: block.type,
                    page: i + 1,
                    variantLabelKey: variantLabelKey(block),
                });
        })
    );
    return { count, places: Array.from(places.values()), cleaned };
}

export type OwnFonts = ReturnType<typeof findOwnFonts>;

/** Switch: write back what `findOwnFonts` already cleaned. A string changed
 *  since the scan is left as it is. */
export function clearOwnFonts(
    content: LomdaV3Content,
    found: OwnFonts
): LomdaV3Content {
    const pages = content.pages.map((page) => {
        const blocks = mapBlockText(
            page.blocks,
            (html, inHeadingField) =>
                found.cleaned[inHeadingField ? 1 : 0].get(html)?.html ?? html
        );
        return blocks === page.blocks ? page : { ...page, blocks };
    });
    return pages.some((p, i) => p !== content.pages[i])
        ? { ...content, pages }
        : content;
}
